import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import * as api from '../services/api';
import { isDevMode } from '../services/auth';

export const PlatformContext = createContext();

// Default UUIDs — must match auth.js
const DEFAULT_ORG_UUID = '550e8400-e29b-41d4-a716-446655440000';
const DEFAULT_TENANT_UUID = '550e8400-e29b-41d4-a716-446655440001';
const SECONDARY_TENANT_UUID = '550e8400-e29b-41d4-a716-446655440002';

// Dedicated System Org/Tenant — owns all system workflows and agents
const SYSTEM_ORG_UUID = '00000000-0000-0000-0000-000000000010';
const SYSTEM_TENANT_UUID = '00000000-0000-0000-0000-000000000011';

// ─── Dev mode fallback data ─────────────────────────────────────────────────
const DEV_ORGS = [
  { id: DEFAULT_ORG_UUID, name: 'Agentlets Corp', domain: 'agentlets.ai', status: 'active', createdAt: '2025-01-15T10:00:00Z' },
];

const DEV_TENANTS = [
  { id: DEFAULT_TENANT_UUID, name: 'Engineering', orgId: DEFAULT_ORG_UUID, status: 'active', createdAt: '2025-01-15T10:00:00Z' },
  { id: SECONDARY_TENANT_UUID, name: 'QA', orgId: DEFAULT_ORG_UUID, status: 'active', createdAt: '2025-02-01T10:00:00Z' },
];

const DEFAULT_INSTANCE_UUID_1 = '550e8400-e29b-41d4-a716-446655440010';
const DEFAULT_INSTANCE_UUID_2 = '550e8400-e29b-41d4-a716-446655440011';
const DEFAULT_INSTANCE_UUID_3 = '550e8400-e29b-41d4-a716-446655440012';
const DEFAULT_INSTANCE_UUID_4 = '550e8400-e29b-41d4-a716-446655440013';

const DEV_INSTANCES = [
  { id: DEFAULT_INSTANCE_UUID_1, name: 'Production', tenantId: DEFAULT_TENANT_UUID, orgId: DEFAULT_ORG_UUID, type: 'production', status: 'active', createdAt: '2025-01-15T10:00:00Z' },
  { id: DEFAULT_INSTANCE_UUID_2, name: 'Production-DR', tenantId: DEFAULT_TENANT_UUID, orgId: DEFAULT_ORG_UUID, type: 'production-dr', status: 'active', createdAt: '2025-01-15T10:00:00Z' },
  { id: DEFAULT_INSTANCE_UUID_3, name: 'Staging', tenantId: DEFAULT_TENANT_UUID, orgId: DEFAULT_ORG_UUID, type: 'staging', status: 'active', createdAt: '2025-01-16T10:00:00Z' },
  { id: DEFAULT_INSTANCE_UUID_4, name: 'Development', tenantId: SECONDARY_TENANT_UUID, orgId: DEFAULT_ORG_UUID, type: 'development', status: 'active', createdAt: '2025-02-01T10:00:00Z' },
];

const DEV_USERS = [
  { id: 'u1', name: 'Alice Chen', firstName: 'Alice', lastName: 'Chen', email: 'alice.chen@corp.io', role: 'Super Admin', status: 'active', orgId: DEFAULT_ORG_UUID, tenantId: DEFAULT_TENANT_UUID, createdAt: '2025-01-15T10:00:00Z' },
  { id: 'u2', name: 'Bob Martinez', firstName: 'Bob', lastName: 'Martinez', email: 'bob.martinez@corp.io', role: 'Org Admin', status: 'active', orgId: DEFAULT_ORG_UUID, tenantId: DEFAULT_TENANT_UUID, createdAt: '2025-01-15T10:05:00Z' },
  { id: 'u3', name: 'Fatima Al-Rashid', firstName: 'Fatima', lastName: 'Al-Rashid', email: 'fatima.r@corp.io', role: 'Tenant Admin', status: 'active', orgId: DEFAULT_ORG_UUID, tenantId: DEFAULT_TENANT_UUID, createdAt: '2025-01-16T10:00:00Z' },
  { id: 'u4', name: 'Chen Wei', firstName: 'Chen', lastName: 'Wei', email: 'chen.wei@corp.io', role: 'Developer', status: 'active', orgId: DEFAULT_ORG_UUID, tenantId: DEFAULT_TENANT_UUID, createdAt: '2025-01-17T10:00:00Z' },
  { id: 'u5', name: 'Sony Abraham', firstName: 'Sony', lastName: 'Abraham', email: 'abraham@agentlets.ai', role: 'Org Admin', status: 'active', orgId: DEFAULT_ORG_UUID, tenantId: DEFAULT_TENANT_UUID, createdAt: '2025-01-15T10:02:00Z' },
];

const DEV_HIERARCHY = [
  {
    orgId: DEFAULT_ORG_UUID, orgName: 'Agentlets Corp', domain: 'agentlets.ai',
    tenantCount: 2,
    tenants: [
      {
        tenantId: DEFAULT_TENANT_UUID, tenantName: 'Engineering', tenantStatus: 'active',
        instanceCount: 3,
        instances: [
          { instanceId: DEFAULT_INSTANCE_UUID_1, instanceName: 'Production', type: 'production', status: 'ACTIVE', deploymentCount: 0, deployments: [] },
          { instanceId: DEFAULT_INSTANCE_UUID_2, instanceName: 'Production-DR', type: 'production-dr', status: 'ACTIVE', deploymentCount: 0, deployments: [] },
          { instanceId: DEFAULT_INSTANCE_UUID_3, instanceName: 'Staging', type: 'staging', status: 'ACTIVE', deploymentCount: 0, deployments: [] },
        ],
      },
      {
        tenantId: SECONDARY_TENANT_UUID, tenantName: 'QA', tenantStatus: 'active',
        instanceCount: 1,
        instances: [
          { instanceId: DEFAULT_INSTANCE_UUID_4, instanceName: 'Development', type: 'development', status: 'ACTIVE', deploymentCount: 0, deployments: [] },
        ],
      },
    ],
  },
];

export function PlatformProvider({ children }) {
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [instances, setInstances] = useState([]);

  const [dashboardMetrics, setDashboardMetrics] = useState(null);

  const [inferenceOverrides, setInferenceOverrides] = useState(null);
  const [inferenceStats, setInferenceStats] = useState(null);
  const [inferenceAuditLog, setInferenceAuditLog] = useState([]);

  // Lifecycle: Phase Gates, Skill Reviews, Assignments, Publications
  const [phaseGates, setPhaseGates] = useState([]);
  const [skillReviews, setSkillReviews] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [publications, setPublications] = useState([]);

  // Workflow & Orchestration
  const [workflows, setWorkflows] = useState([]);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [orchestrationStats, setOrchestrationStats] = useState(null);

  // Connector Groups & Agent-Connector Assignments
  const [connectorGroups, setConnectorGroups] = useState([]);
  const [connectorAssignments, setConnectorAssignments] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAgents = useCallback(async ({ orgId, tenantId } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getAgents({ orgId, tenantId });
      if (result.ok) {
        setAgents(result.data || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const result = await api.getTenants();
      if (result.ok) {
        setTenants(result.data || []);
      } else if (isDevMode()) {
        setTenants(DEV_TENANTS);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      if (isDevMode()) setTenants(DEV_TENANTS);
    }
  }, []);

  const loadDeployments = useCallback(async ({ orgId, tenantId, agentId, environment, status } = {}) => {
    try {
      const result = await api.getDeployments({ orgId, tenantId, agentId, environment, status });
      if (result.ok) {
        setDeployments(result.data || []);
      }
    } catch (err) {
      console.error('Error loading deployments:', err);
    }
  }, []);

  const loadConnectors = useCallback(async ({ tenantId, orgId, type, status } = {}) => {
    try {
      const result = await api.getConnectors({ tenantId, orgId, type, status });
      if (result.ok) {
        /* api.getConnectors unwraps the sharing envelope and always returns an array,
         * with __isOwner / __sharedWithTenantIds / __sharedWithGrants carried through. */
        const flattened = Array.isArray(result.data) ? result.data : [];
        setConnectors(flattened);
      }
    } catch (err) {
      console.error('Error loading connectors:', err);
    }
  }, []);

  const loadUsers = useCallback(async ({ tenantId } = {}) => {
    try {
      const result = await api.getUsers({ tenantId });
      if (result.ok) {
        setUsers(result.data || []);
      } else if (isDevMode()) {
        setUsers(DEV_USERS);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      if (isDevMode()) setUsers(DEV_USERS);
    }
  }, []);

  const loadGroups = useCallback(async ({ tenantId } = {}) => {
    try {
      const result = await api.getGroups({ tenantId });
      if (result.ok) {
        setGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  }, []);

  const createUser = useCallback(async (payload) => {
    try {
      const result = await api.createUser(payload);
      if (result.ok) {
        await loadUsers();
        return result;
      }
      if (isDevMode()) {
        const newUser = { id: `u-${Date.now()}`, ...payload, name: `${payload.firstName || ''} ${payload.lastName || ''}`.trim(), status: 'active', createdAt: new Date().toISOString() };
        setUsers((prev) => [...prev, newUser]);
        return { ok: true, data: newUser };
      }
      return result;
    } catch (err) {
      console.error('Error creating user:', err);
      if (isDevMode()) {
        const newUser = { id: `u-${Date.now()}`, ...payload, name: `${payload.firstName || ''} ${payload.lastName || ''}`.trim(), status: 'active', createdAt: new Date().toISOString() };
        setUsers((prev) => [...prev, newUser]);
        return { ok: true, data: newUser };
      }
      return { ok: false, error: err.message };
    }
  }, [loadUsers]);

  const deleteUser = useCallback(async (userId) => {
    try {
      const result = await api.deleteUser(userId);
      if (result.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        return result;
      }
      if (isDevMode()) {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        return { ok: true };
      }
      return result;
    } catch (err) {
      console.error('Error deleting user:', err);
      if (isDevMode()) {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const createGroup = useCallback(async (payload) => {
    try {
      const result = await api.createGroup(payload);
      if (result.ok) {
        await loadGroups();
      }
      return result;
    } catch (err) {
      console.error('Error creating group:', err);
      return { ok: false, error: err.message };
    }
  }, [loadGroups]);

  const deleteGroup = useCallback(async (groupId) => {
    try {
      const result = await api.deleteGroup(groupId);
      if (result.ok) {
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
      }
      return result;
    } catch (err) {
      console.error('Error deleting group:', err);
      return { ok: false, error: err.message };
    }
  }, []);

  const updateUser = useCallback(async (userId, payload) => {
    try {
      const result = await api.updateUser(userId, payload);
      if (result.ok) {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...result.data } : u)));
        return result;
      }
      if (isDevMode()) {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...payload } : u)));
        return { ok: true };
      }
      return result;
    } catch (err) {
      console.error('Error updating user:', err);
      if (isDevMode()) {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...payload } : u)));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const updateGroup = useCallback(async (groupId, payload) => {
    try {
      const result = await api.updateGroup(groupId, payload);
      if (result.ok) {
        await loadGroups();
      }
      return result;
    } catch (err) {
      console.error('Error updating group:', err);
      return { ok: false, error: err.message };
    }
  }, [loadGroups]);

  const addGroupMember = useCallback(async (groupId, userId) => {
    try {
      const result = await api.addGroupMember(groupId, userId);
      if (result.ok) {
        await loadGroups();
      }
      return result;
    } catch (err) {
      console.error('Error adding group member:', err);
      return { ok: false, error: err.message };
    }
  }, [loadGroups]);

  const removeGroupMember = useCallback(async (groupId, userId) => {
    try {
      const result = await api.removeGroupMember(groupId, userId);
      if (result.ok) {
        await loadGroups();
      }
      return result;
    } catch (err) {
      console.error('Error removing group member:', err);
      return { ok: false, error: err.message };
    }
  }, [loadGroups]);

  const loadDashboardMetrics = useCallback(async ({ orgId, tenantId } = {}) => {
    try {
      const result = await api.getDashboardMetrics(orgId, tenantId);
      if (result.ok) {
        setDashboardMetrics(result.data);
      }
    } catch (err) {
      console.error('Error loading dashboard metrics:', err);
    }
  }, []);

  const loadEvals = useCallback(async (agentId) => {
    try {
      const result = await api.getEvals(agentId);
      if (result.ok) {
        return result.data || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading evals:', err);
      return [];
    }
  }, []);

  const generateEval = useCallback(async (agentId, payload) => {
    try {
      return await api.generateEval(agentId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const runEval = useCallback(async (agentId, evalId) => {
    try {
      return await api.runEval(agentId, evalId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const getEvalTestCases = useCallback(async (agentId, evalId) => {
    try {
      const result = await api.getEvalTestCases(agentId, evalId);
      if (result.ok) return result.data || [];
      return [];
    } catch (err) {
      console.error('Error loading test cases:', err);
      return [];
    }
  }, []);

  const getEvalSuite = useCallback(async (agentId, evalId) => {
    try {
      const result = await api.getEvalSuite(agentId, evalId);
      if (result.ok) return result.data;
      return null;
    } catch (err) { return null; }
  }, []);

  const getEvalRuns = useCallback(async (agentId, evalId) => {
    try {
      const result = await api.getEvalRuns(agentId, evalId);
      if (result.ok) return result.data || [];
      return [];
    } catch (err) { return []; }
  }, []);

  const updateEvalSuite = useCallback(async (agentId, evalId, payload) => {
    try {
      return await api.updateEvalSuite(agentId, evalId, payload);
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const updateTestCase = useCallback(async (agentId, evalId, testCaseId, payload) => {
    try {
      return await api.updateTestCase(agentId, evalId, testCaseId, payload);
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const getLlmProviders = useCallback(async (agentId) => {
    try {
      const result = await api.getLlmProviders(agentId);
      if (result.ok) return result.data || [];
      return [];
    } catch (err) { return []; }
  }, []);

  const deleteEvalSuite = useCallback(async (agentId, evalId) => {
    try {
      return await api.deleteEvalSuite(agentId, evalId);
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const loadExecutions = useCallback(async (agentId) => {
    try {
      const result = await api.getExecutions(agentId);
      if (result.ok) {
        setExecutions(result.data || []);
        return result.data || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading executions:', err);
      return [];
    }
  }, []);

  const loadInferenceData = useCallback(async (agentId) => {
    try {
      const [overrides, stats, auditLog] = await Promise.all([
        api.getOverrides(agentId),
        api.getInferenceStats(agentId),
        api.getInferenceAuditLog(agentId),
      ]);

      if (overrides.ok) setInferenceOverrides(overrides.data);
      if (stats.ok) setInferenceStats(stats.data);
      if (auditLog.ok) setInferenceAuditLog(Array.isArray(auditLog.data) ? auditLog.data : []);
    } catch (err) {
      console.error('Error loading inference data:', err);
    }
  }, []);

  const createAgent = useCallback(async (payload) => {
    try {
      const result = await api.createAgent(payload);
      if (result.ok) {
        setAgents((prev) => [...prev, result.data]);
        return result;
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const updateAgent = useCallback(async (agentId, payload) => {
    try {
      const result = await api.updateAgent(agentId, payload);
      if (result.ok) {
        setAgents((prev) =>
          prev.map((a) => (a.id === agentId ? result.data : a))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteAgent = useCallback(async (agentId) => {
    try {
      const result = await api.deleteAgent(agentId);
      if (result.ok) {
        setAgents((prev) => prev.filter((a) => a.id !== agentId));
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const addSkill = useCallback(async (agentId, payload) => {
    try {
      return await api.addSkill(agentId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const updateSkill = useCallback(async (agentId, skillId, payload) => {
    try {
      return await api.editSkill(agentId, skillId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const removeSkill = useCallback(async (agentId, skillId) => {
    try {
      return await api.deleteSkill(agentId, skillId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  // Explicit unpublish action — clears wasPublished/pushed flags and
  // triggers an agentlet re-sync. Required step before deleting any
  // skill that has previously been pushed to a gateway. See
  // PublishedAssetDeletionException for the rule.
  const unpublishSkill = useCallback(async (agentId, skillId) => {
    try {
      return await api.unpublishSkill(agentId, skillId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const pushSkill = useCallback(async (agentId, skillId) => {
    try {
      return await api.pushSkill(agentId, skillId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const pushAllSkills = useCallback(async (agentId) => {
    try {
      return await api.pushAllSkills(agentId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const syncToAgentlet = useCallback(async (agentId) => {
    try {
      return await api.syncToAgentlet(agentId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const promoteSkill = useCallback(async (agentId, skillId, payload) => {
    try {
      return await api.promoteSkill(agentId, skillId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Phase Gate Lifecycle
  // ---------------------------------------------------------------------------
  const advancePhase = useCallback(async (agentId, userId) => {
    try {
      const result = await api.advancePhase(agentId, userId);
      if (result.ok) {
        // Update the agent in local state with the returned agent data
        if (result.data?.agent) {
          setAgents((prev) =>
            prev.map((a) => (a.id === agentId ? result.data.agent : a))
          );
        }
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const advanceSkill = useCallback(async (agentId, skillId) => {
    try {
      const result = await api.advanceSkill(agentId, skillId);
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const regressSkill = useCallback(async (agentId, skillId) => {
    try {
      const result = await api.regressSkill(agentId, skillId);
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadPhaseGates = useCallback(async (agentId) => {
    try {
      const result = await api.getPhaseGates(agentId);
      if (result.ok) {
        setPhaseGates(result.data || []);
        return result.data || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading phase gates:', err);
      return [];
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Skill Reviews (Decide Phase)
  // ---------------------------------------------------------------------------
  const reviewSkill = useCallback(async (agentId, skillId, payload) => {
    try {
      return await api.reviewSkill(agentId, skillId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadSkillReviews = useCallback(async (agentId) => {
    try {
      const result = await api.getSkillReviews(agentId);
      if (result.ok) {
        setSkillReviews(result.data || []);
        return result.data || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading skill reviews:', err);
      return [];
    }
  }, []);

  // ---------------------------------------------------------------------------
  // User/Group Assignments (Act Phase)
  // ---------------------------------------------------------------------------
  // v5.3.0 — Merge per-agent assignments instead of replacing entire array
  // (rollback: revert to `setAssignments(result.data || [])`)
  const loadAssignments = useCallback(async (agentId) => {
    try {
      const result = await api.getAssignments(agentId);
      if (result.ok) {
        const fresh = result.data || [];
        setAssignments((prev) => {
          // Remove stale entries for this agent, then merge fresh ones
          const other = prev.filter((a) => a.agentId !== agentId);
          return [...other, ...fresh];
        });
        return fresh;
      }
      return [];
    } catch (err) {
      console.error('Error loading assignments:', err);
      return [];
    }
  }, []);

  // v5.3.0 — Deduplicate on append (rollback: revert to simple [...prev, result.data])
  const createAssignment = useCallback(async (agentId, payload) => {
    try {
      const result = await api.createAssignment(agentId, payload);
      if (result.ok) {
        setAssignments((prev) => {
          // Prevent duplicate entries
          const exists = prev.some((a) => a.id === result.data.id);
          return exists ? prev : [...prev, result.data];
        });
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const removeAssignment = useCallback(async (agentId, assignmentId) => {
    try {
      const result = await api.removeAssignment(agentId, assignmentId);
      if (result.ok) {
        setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Publish (Act Phase)
  // ---------------------------------------------------------------------------
  const publishAgent = useCallback(async (agentId, userId) => {
    try {
      const result = await api.publishAgent(agentId, userId);
      if (result.ok) {
        setPublications((prev) => [result.data, ...prev]);
        // Update agent published state
        setAgents((prev) =>
          prev.map((a) =>
            a.id === agentId ? { ...a, published: true, publishVersion: result.data.version } : a
          )
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadPublications = useCallback(async (agentId, page, size = 10) => {
    try {
      const result = await api.getPublications(agentId, page, size);
      if (result.ok) {
        // Paginated response has { content, totalElements, totalPages, number, size }
        const data = result.data;
        if (data && data.content) {
          setPublications(data.content);
          return data; // Return full page metadata
        }
        // Backward compat: non-paginated response is a plain array
        setPublications(data || []);
        return { content: data || [], totalElements: (data || []).length, totalPages: 1, number: 0 };
      }
      return { content: [], totalElements: 0, totalPages: 0, number: 0 };
    } catch (err) {
      console.error('Error loading publications:', err);
      return { content: [], totalElements: 0, totalPages: 0, number: 0 };
    }
  }, []);

  const rollbackPublication = useCallback(async (agentId, pubId, userId) => {
    try {
      const result = await api.rollbackPublication(agentId, pubId, userId);
      if (result.ok) {
        setPublications((prev) => [result.data, ...prev]);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Workflow & Orchestration
  // ---------------------------------------------------------------------------
  // V132 — `archived` is forwarded to the list endpoint:
  //   undefined (default) → non-archived only (matches backend default)
  //   'all'               → non-archived + archived (list view with Show Archived ON)
  //   'true'              → only archived (dedicated Archived view)
  const loadWorkflows = useCallback(async ({ orgId, tenantId, agentId, archived } = {}) => {
    try {
      const result = await api.getWorkflows({ orgId, tenantId, agentId, archived });
      if (result.ok) {
        setWorkflows(result.data || []);
        return result.data || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading workflows:', err);
      return [];
    }
  }, []);

  const createWorkflow = useCallback(async (payload) => {
    try {
      const result = await api.createWorkflow(payload);
      if (result.ok) {
        setWorkflows((prev) => [result.data, ...prev]);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const updateWorkflow = useCallback(async (workflowId, payload) => {
    try {
      const result = await api.updateWorkflow(workflowId, payload);
      if (result.ok) {
        setWorkflows((prev) =>
          prev.map((w) => (w.id === workflowId ? result.data : w))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteWorkflow = useCallback(async (workflowId) => {
    try {
      const result = await api.deleteWorkflow(workflowId);
      if (result.ok) {
        setWorkflows((prev) => prev.filter((w) => w.id !== workflowId));
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  // RC-134 — forward the optimistic-lock opts ({ changeKind, ifMatchVersion })
  // through to the API. This wrapper previously took only (workflowId,
  // canvasData) and silently dropped the third arg, so the If-Match version
  // guard never reached the wire and the 412 conflict flow was inert (a stale
  // tab could last-write-win over a concurrent save). Additive: callers that
  // pass no opts get undefined → api.saveCanvas's {} default → legacy behavior.
  const saveCanvas = useCallback(async (workflowId, canvasData, opts) => {
    try {
      const result = await api.saveCanvas(workflowId, canvasData, opts);
      if (result.ok) {
        setWorkflows((prev) =>
          prev.map((w) => (w.id === workflowId ? result.data : w))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const executeWorkflow = useCallback(async (workflowId, payload) => {
    try {
      const result = await api.executeWorkflow(workflowId, payload);
      if (result.ok) {
        setWorkflowRuns((prev) => [result.data, ...prev]);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadWorkflowRuns = useCallback(async (workflowId) => {
    try {
      const result = await api.getWorkflowRuns(workflowId);
      if (result.ok) {
        setWorkflowRuns(result.data || []);
        return result.data || [];
      }
      return [];
    } catch (err) {
      console.error('Error loading workflow runs:', err);
      return [];
    }
  }, []);

  const cancelWorkflowRun = useCallback(async (runId) => {
    try {
      const result = await api.cancelWorkflowRun(runId);
      if (result.ok) {
        setWorkflowRuns((prev) =>
          prev.map((r) => (r.id === runId ? result.data : r))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadOrchestrationStats = useCallback(async (orgId, tenantId) => {
    try {
      const result = await api.getOrchestrationStats(orgId, tenantId);
      if (result.ok) {
        setOrchestrationStats(result.data);
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('Error loading orchestration stats:', err);
      return null;
    }
  }, []);

  const createDeployment = useCallback(async (payload) => {
    try {
      const result = await api.createDeployment(payload);
      if (result.ok) {
        setDeployments((prev) => [...prev, result.data]);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const updateDeployment = useCallback(async (deploymentId, payload) => {
    try {
      const result = await api.updateDeployment(deploymentId, payload);
      if (result.ok) {
        setDeployments((prev) =>
          prev.map((d) => (d.id === deploymentId ? result.data : d))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteDeployment = useCallback(async (deploymentId) => {
    try {
      const result = await api.deleteDeployment(deploymentId);
      if (result.ok) {
        setDeployments((prev) => prev.filter((d) => d.id !== deploymentId));
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const updateDeploymentStatus = useCallback(async (deploymentId, status) => {
    try {
      const result = await api.updateDeploymentStatus(deploymentId, status);
      if (result.ok) {
        setDeployments((prev) =>
          prev.map((d) => (d.id === deploymentId ? result.data : d))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadDeploymentHierarchy = useCallback(async (orgId) => {
    try {
      const result = await api.getDeploymentHierarchy(orgId);
      if (result.ok) {
        return result.data || [];
      }
      if (isDevMode()) return DEV_HIERARCHY;
      return [];
    } catch (err) {
      console.error('Error loading deployment hierarchy:', err);
      if (isDevMode()) return DEV_HIERARCHY;
      return [];
    }
  }, []);

  const deployFromAgent = useCallback(async (agentId, config) => {
    try {
      const result = await api.deployFromAgent(agentId, config);
      if (result.ok) {
        setDeployments((prev) => [result.data, ...prev]);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadDeploymentStats = useCallback(async (orgId, tenantId) => {
    try {
      const result = await api.getDeploymentStats(orgId, tenantId);
      if (result.ok) {
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('Error loading deployment stats:', err);
      return null;
    }
  }, []);

  const loadDeploymentHealth = useCallback(async (deploymentId) => {
    try {
      const result = await api.getDeploymentHealth(deploymentId);
      if (result.ok) {
        return result.data;
      }
      return null;
    } catch (err) {
      console.error('Error loading deployment health:', err);
      return null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Instances  (Org → Tenant → Instance)
  // ---------------------------------------------------------------------------
  const loadInstances = useCallback(async (tenantId) => {
    try {
      const result = await api.getInstances(tenantId);
      if (result.ok) {
        setInstances(result.data || []);
      } else if (isDevMode()) {
        setInstances(tenantId ? DEV_INSTANCES.filter((i) => i.tenantId === tenantId) : DEV_INSTANCES);
      }
    } catch (err) {
      console.error('Error loading instances:', err);
      if (isDevMode()) setInstances(DEV_INSTANCES);
    }
  }, []);

  const createInstance = useCallback(async (payload) => {
    try {
      const result = await api.createInstance(payload);
      if (result.ok) {
        setInstances((prev) => [...prev, result.data]);
        return result;
      }
      if (isDevMode()) {
        const newInst = { id: `inst-${Date.now()}`, ...payload, status: 'active', createdAt: new Date().toISOString() };
        setInstances((prev) => [...prev, newInst]);
        return { ok: true, data: newInst };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        const newInst = { id: `inst-${Date.now()}`, ...payload, status: 'active', createdAt: new Date().toISOString() };
        setInstances((prev) => [...prev, newInst]);
        return { ok: true, data: newInst };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const updateInstance = useCallback(async (instanceId, payload) => {
    try {
      const result = await api.updateInstance(instanceId, payload);
      if (result.ok) {
        setInstances((prev) => prev.map((i) => (i.id === instanceId ? result.data : i)));
        return result;
      }
      if (isDevMode()) {
        setInstances((prev) => prev.map((i) => (i.id === instanceId ? { ...i, ...payload } : i)));
        return { ok: true, data: { id: instanceId, ...payload } };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setInstances((prev) => prev.map((i) => (i.id === instanceId ? { ...i, ...payload } : i)));
        return { ok: true, data: { id: instanceId, ...payload } };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteInstance = useCallback(async (instanceId) => {
    try {
      const result = await api.deleteInstance(instanceId);
      if (result.ok) {
        setInstances((prev) => prev.filter((i) => i.id !== instanceId));
        return result;
      }
      if (isDevMode()) {
        setInstances((prev) => prev.filter((i) => i.id !== instanceId));
        return { ok: true };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setInstances((prev) => prev.filter((i) => i.id !== instanceId));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Organizations
  // ---------------------------------------------------------------------------
  const loadOrganizations = useCallback(async () => {
    try {
      const result = await api.getOrganizations();
      if (result.ok) {
        setOrgs(result.data || []);
      } else if (isDevMode()) {
        setOrgs(DEV_ORGS);
      }
    } catch (err) {
      console.error('Error loading organizations:', err);
      if (isDevMode()) setOrgs(DEV_ORGS);
    }
  }, []);

  const createOrganization = useCallback(async (payload) => {
    try {
      const result = await api.createOrganization(payload);
      if (result.ok) {
        setOrgs((prev) => [...prev, result.data]);
        return result;
      }
      if (isDevMode()) {
        const newOrg = { id: `org-${Date.now()}`, ...payload, status: 'active', createdAt: new Date().toISOString() };
        setOrgs((prev) => [...prev, newOrg]);
        return { ok: true, data: newOrg };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        const newOrg = { id: `org-${Date.now()}`, ...payload, status: 'active', createdAt: new Date().toISOString() };
        setOrgs((prev) => [...prev, newOrg]);
        return { ok: true, data: newOrg };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const updateOrganization = useCallback(async (orgId, payload) => {
    try {
      const result = await api.updateOrganization(orgId, payload);
      if (result.ok) {
        setOrgs((prev) => prev.map((o) => (o.id === orgId ? result.data : o)));
        return result;
      }
      if (isDevMode()) {
        setOrgs((prev) => prev.map((o) => (o.id === orgId ? { ...o, ...payload } : o)));
        return { ok: true };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setOrgs((prev) => prev.map((o) => (o.id === orgId ? { ...o, ...payload } : o)));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteOrganization = useCallback(async (orgId) => {
    try {
      const result = await api.deleteOrganization(orgId);
      if (result.ok) {
        setOrgs((prev) => prev.filter((o) => o.id !== orgId));
        return result;
      }
      if (isDevMode()) {
        setOrgs((prev) => prev.filter((o) => o.id !== orgId));
        return { ok: true };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setOrgs((prev) => prev.filter((o) => o.id !== orgId));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const createConnector = useCallback(async (payload) => {
    try {
      const result = await api.createConnector(payload);
      if (result.ok) {
        setConnectors((prev) => [...prev, result.data]);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const updateConnector = useCallback(async (connectorId, payload) => {
    try {
      const result = await api.updateConnector(connectorId, payload);
      if (result.ok) {
        setConnectors((prev) =>
          prev.map((c) => (c.id === connectorId ? result.data : c))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteConnector = useCallback(async (connectorId) => {
    try {
      const result = await api.deleteConnector(connectorId);
      if (result.ok) {
        setConnectors((prev) => prev.filter((c) => c.id !== connectorId));
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const testConnector = useCallback(async (connectorId) => {
    try {
      const result = await api.testConnector(connectorId);
      if (result.ok) {
        setConnectors((prev) =>
          prev.map((c) => (c.id === connectorId ? result.data : c))
        );
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const testConnectorUrl = useCallback(async (url) => {
    try {
      const result = await api.testConnectorUrl(url);
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadConnectorStats = useCallback(async (orgId, tenantId) => {
    try {
      const result = await api.getConnectorStats(orgId, tenantId);
      if (result.ok) return result.data;
      return null;
    } catch (err) {
      console.error('Error loading connector stats:', err);
      return null;
    }
  }, []);

  // Skill ↔ Connector linking
  const loadSkillConnectors = useCallback(async (agentId, skillId) => {
    try {
      const result = await api.getSkillConnectors(agentId, skillId);
      if (result.ok) return result.data || [];
      return [];
    } catch (err) {
      console.error('Error loading skill connectors:', err);
      return [];
    }
  }, []);

  const linkSkillConnector = useCallback(async (agentId, skillId, payload) => {
    try {
      return await api.linkSkillConnector(agentId, skillId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const unlinkSkillConnector = useCallback(async (agentId, skillId, connectorId) => {
    try {
      return await api.unlinkSkillConnector(agentId, skillId, connectorId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const loadConnectorSkills = useCallback(async (connectorId) => {
    try {
      const result = await api.getConnectorSkills(connectorId);
      if (result.ok) return result.data || [];
      return [];
    } catch (err) {
      console.error('Error loading connector skills:', err);
      return [];
    }
  }, []);

  const createTenant = useCallback(async (payload) => {
    try {
      const result = await api.createTenant(payload);
      if (result.ok) {
        setTenants((prev) => [...prev, result.data]);
        return result;
      }
      if (isDevMode()) {
        const newTenant = { id: `t-${Date.now()}`, ...payload, createdAt: new Date().toISOString() };
        setTenants((prev) => [...prev, newTenant]);
        return { ok: true, data: newTenant };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        const newTenant = { id: `t-${Date.now()}`, ...payload, createdAt: new Date().toISOString() };
        setTenants((prev) => [...prev, newTenant]);
        return { ok: true, data: newTenant };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const updateTenant = useCallback(async (tenantId, payload) => {
    try {
      const result = await api.updateTenant(tenantId, payload);
      if (result.ok) {
        setTenants((prev) => prev.map((t) => (t.id === tenantId ? result.data : t)));
        return result;
      }
      if (isDevMode()) {
        setTenants((prev) => prev.map((t) => (t.id === tenantId ? { ...t, ...payload } : t)));
        return { ok: true };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setTenants((prev) => prev.map((t) => (t.id === tenantId ? { ...t, ...payload } : t)));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const deleteTenant = useCallback(async (tenantId) => {
    try {
      const result = await api.deleteTenant(tenantId);
      if (result.ok) {
        setTenants((prev) => prev.filter((t) => t.id !== tenantId));
        return result;
      }
      if (isDevMode()) {
        setTenants((prev) => prev.filter((t) => t.id !== tenantId));
        return { ok: true };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setTenants((prev) => prev.filter((t) => t.id !== tenantId));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  const approveExecution = useCallback(async (executionId, payload) => {
    try {
      return await api.approveExecution(executionId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const rejectExecution = useCallback(async (executionId, payload) => {
    try {
      return await api.rejectExecution(executionId, payload);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const activateKillSwitch = useCallback(async (agentId, reason) => {
    try {
      return await api.activateKillSwitch(agentId, { reason });
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const deactivateKillSwitch = useCallback(async (agentId) => {
    try {
      return await api.deactivateKillSwitch(agentId);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const setInferenceMode = useCallback(async (agentId, mode) => {
    try {
      const result = await api.setInferenceMode(agentId, mode);
      if (result.ok) {
        setInferenceOverrides(result.data);
      }
      return result;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  const setSkillInferenceMode = useCallback(async (agentId, skillId, mode) => {
    try {
      return await api.setSkillInferenceMode(agentId, skillId, mode);
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }, []);

  // ── Connector Groups ──────────────────────────────────────────────────
  const loadConnectorGroups = useCallback(async (tenantId) => {
    try {
      const result = await api.getConnectorGroups(tenantId);
      if (result.ok) setConnectorGroups(result.data || []);
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const createConnectorGroup = useCallback(async (payload) => {
    try { return await api.createConnectorGroup(payload); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const updateConnectorGroup = useCallback(async (groupId, payload) => {
    try { return await api.updateConnectorGroup(groupId, payload); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const deleteConnectorGroup = useCallback(async (groupId) => {
    try { return await api.deleteConnectorGroup(groupId); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const addConnectorGroupMember = useCallback(async (groupId, connectorId) => {
    try { return await api.addConnectorGroupMember(groupId, connectorId); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const removeConnectorGroupMember = useCallback(async (groupId, connectorId) => {
    try { return await api.removeConnectorGroupMember(groupId, connectorId); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  // ── Agent-Connector Assignments ────────────────────────────────────────
  const loadConnectorAssignments = useCallback(async (agentId) => {
    try {
      const result = await api.getAgentConnectorAssignments(agentId);
      if (result.ok) {
        setConnectorAssignments((prev) => {
          // Merge: keep other agents' assignments, replace this agent's
          const other = prev.filter((a) => a.agentId !== agentId);
          return [...other, ...(result.data || [])];
        });
      }
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const createConnectorAssignment = useCallback(async (agentId, payload) => {
    try { return await api.createAgentConnectorAssignment(agentId, payload); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const removeConnectorAssignment = useCallback(async (agentId, assignmentId) => {
    try { return await api.removeAgentConnectorAssignment(agentId, assignmentId); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const getAssignedConnectorIds = useCallback(async (agentId) => {
    try { return await api.getAssignedConnectorIds(agentId); }
    catch (err) { return { ok: false, error: err.message }; }
  }, []);

  // ---------------------------------------------------------------------------
  // Invitations
  // ---------------------------------------------------------------------------
  const [invitations, setInvitations] = useState([]);

  // ─── Invite Email Workflow ──────────────────────────────────────────────
  // System workflow that sends a professional HTML invitation email via
  // the SMTP email connector, then is executed by the workflow engine.
  const INVITE_WORKFLOW_NAME = 'System: User Invitation Email';

  const buildInviteEmailWorkflowDef = () => ({
    nodes: [
      {
        id: 'trigger-manual',
        type: 'manual_trigger',
        label: 'Invitation Triggered',
        x: 80, y: 200,
        config: {
          description: 'Triggered when a user invitation is sent from Team Management',
          outputVariable: 'trigger-manual',
        },
      },
      {
        id: 'send-invite-email',
        type: 'notification_task',
        label: 'Send Invitation Email',
        x: 400, y: 200,
        config: {
          channel: 'email',
          recipient: '${invitee_email}',
          subject: 'You\'ve Been Invited to Join Prove7 Control Vector',
          content_type: 'html',
          body: [
            '<div style="font-family:Inter,system-ui,sans-serif;max-width:600px;margin:0 auto;background:#fff;border:1px solid #e7e5e4;border-radius:12px;overflow:hidden">',
            '<div style="background:linear-gradient(135deg,#0d9488 0%,#0a766b 100%);padding:32px;text-align:center">',
            '<h1 style="color:#fff;font-size:22px;margin:0">Welcome to Agentlets</h1>',
            '<p style="color:rgba(255,255,255,0.85);font-size:13px;margin:6px 0 0">Trusted Autonomy Platform</p>',
            '</div>',
            '<div style="padding:32px">',
            '<p style="font-size:15px;color:#18181b;margin:0 0 20px">Hi <strong>${invitee_name}</strong>,</p>',
            '<p style="font-size:14px;color:#52525b;line-height:1.6;margin:0 0 20px">You have been invited to join the <strong>Prove7 Control Vector Platform</strong> as <strong>${invitee_role}</strong>. Click the button below to complete your registration and set your password.</p>',
            '<div style="text-align:center;margin:28px 0">',
            '<a href="${registration_url}" style="display:inline-block;padding:14px 36px;background:#0d9488;color:#fff;font-size:14px;font-weight:600;text-decoration:none;border-radius:8px">Complete Registration</a>',
            '</div>',
            '<div style="background:#f5f5f4;border-radius:8px;padding:16px;margin:20px 0">',
            '<div style="font-size:12px;color:#71717a;margin-bottom:8px"><strong>Invitation Details:</strong></div>',
            '<div style="font-size:13px;color:#18181b">Email: <strong>${invitee_email}</strong></div>',
            '<div style="font-size:13px;color:#18181b">Role: <strong>${invitee_role}</strong></div>',
            '<div style="font-size:13px;color:#18181b">Organization: <strong>${org_name}</strong></div>',
            '</div>',
            '<div style="border-top:1px solid #e7e5e4;padding-top:16px;margin-top:24px">',
            '<p style="font-size:11px;color:#a8a29e;margin:0">This invitation link expires in <strong>24 hours</strong>. If you did not expect this invitation, you can safely ignore this email.</p>',
            '<p style="font-size:11px;color:#a8a29e;margin:8px 0 0">If the button doesn\'t work, copy and paste this URL into your browser:</p>',
            '<p style="font-size:10px;color:#78716c;word-break:break-all;margin:4px 0 0">${registration_url}</p>',
            '</div>',
            '</div>',
            '</div>',
          ].join('\n'),
          priority: 'high',
        },
      },
    ],
    connections: [
      { id: 'conn_trigger_to_email', from: 'trigger-manual', fromPort: 'default', to: 'send-invite-email', toPort: 'default' },
    ],
  });

  // Find or create the system invite workflow, then execute it with invite data
  const triggerInviteEmailWorkflow = useCallback(async (inviteData) => {
    try {
      // System workflows live in the dedicated Agentlet System Org/Tenant
      const wfOrgId = SYSTEM_ORG_UUID;
      const wfTenantId = SYSTEM_TENANT_UUID;

      // Look for existing system workflow (must pass orgId+tenantId — backend requires both).
      // V132 — pass archived:'all' so an accidentally-archived system workflow
      // is still found by name (prevents silent orphaning if someone archived
      // the invite flow). The createOrUpdate path below re-activates it.
      let workflowId = null;
      const wfResult = await api.getWorkflows({ orgId: wfOrgId, tenantId: wfTenantId, archived: 'all' });
      if (wfResult.ok) {
        const existing = (wfResult.data || []).find((w) => w.name === INVITE_WORKFLOW_NAME);
        if (existing) workflowId = existing.id;
      }

      // Create if not found; if found, update the definition to latest version
      if (!workflowId) {
        const createResult = await api.createWorkflow({
          name: INVITE_WORKFLOW_NAME,
          description: 'System workflow: sends invitation email to new users with registration link. Triggered automatically by Invite User flow.',
          status: 'active',
          orgId: wfOrgId,
          tenantId: wfTenantId,
          definition: buildInviteEmailWorkflowDef(),
        });
        if (createResult?.ok) workflowId = createResult.data?.id;
      } else {
        // Ensure the existing workflow has the latest definition (connector_task email format)
        await api.updateWorkflow(workflowId, { definition: buildInviteEmailWorkflowDef() }).catch(() => {});
      }

      if (!workflowId) {
        console.warn('[invite-workflow] Could not find or create invite email workflow');
        return { ok: false, error: 'Workflow not available' };
      }

      // Execute the workflow with invite context as input (passed to Temporal OSS executionContext)
      const orgName = inviteData.orgId
        ? (orgs || []).find((o) => o.id === inviteData.orgId)?.name || 'Agentlets'
        : 'Agentlets';

      const execResult = await api.executeWorkflow(workflowId, {
        input: {
          invitee_email: inviteData.email,
          invitee_name: `${inviteData.firstName || ''} ${inviteData.lastName || ''}`.trim() || inviteData.email,
          invitee_role: inviteData.role || 'Viewer',
          registration_url: inviteData.registrationUrl,
          platform_url: window.location.origin,
          org_name: orgName,
        },
      });

      return execResult;
    } catch (err) {
      console.warn('[invite-workflow] Failed to trigger invite email workflow:', err.message);
      return { ok: false, error: err.message };
    }
  }, [orgs]);

  // Pre-invite validation: check if email already exists in backend
  // Returns { exists, user, status } so the caller can decide what to do
  const checkUserEmail = useCallback(async (email) => {
    try {
      // First check local state (already loaded users)
      const localMatch = (users || []).find(
        (u) => u.email?.toLowerCase() === email.toLowerCase()
      );
      if (localMatch) {
        return { exists: true, user: localMatch, status: localMatch.status };
      }

      // Then do a backend fetch to be sure (catches users not yet in local state)
      const result = await api.getUsers({});
      if (result.ok && Array.isArray(result.data)) {
        const backendMatch = result.data.find(
          (u) => u.email?.toLowerCase() === email.toLowerCase()
        );
        if (backendMatch) {
          return { exists: true, user: backendMatch, status: backendMatch.status };
        }
      }

      return { exists: false };
    } catch (err) {
      // If backend check fails, rely on local state only
      const localMatch = (users || []).find(
        (u) => u.email?.toLowerCase() === email.toLowerCase()
      );
      if (localMatch) {
        return { exists: true, user: localMatch, status: localMatch.status };
      }
      return { exists: false };
    }
  }, [users]);

  // Helper: build invite data object with token and registration URL
  // inviteToken = server-generated one-time token stored on user record
  const buildInviteData = (userId, payload, inviteToken) => {
    const inviteData = {
      id: userId,
      ...payload,
      status: 'invited',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    };
    const expiry = Date.now() + 24 * 60 * 60 * 1000;
    const token = btoa(JSON.stringify({ userId, email: payload.email, inviteToken, expiry }));
    inviteData.token = token;
    inviteData.registrationUrl = `${window.location.origin}/register?token=${token}`;
    return inviteData;
  };

  const sendInvitation = useCallback(async (payload) => {
    // ── Step 1: Pre-validate email against backend ─────────────────────
    // Check if user already exists BEFORE attempting to create
    const emailCheck = await checkUserEmail(payload.email);

    if (emailCheck.exists) {
      const existingUser = emailCheck.user;
      const existingStatus = (existingUser.status || 'active').toLowerCase();

      // BLOCK: active users cannot be re-invited
      if (existingStatus === 'active') {
        return {
          ok: false,
          error: `User "${existingUser.name || existingUser.email}" is already an active user with role "${existingUser.role}". Active users cannot be re-invited.`,
          activeUser: true,
        };
      }

      // ALLOW re-invite: inactive or previously invited users
      // Update their status back to 'invited' and generate a fresh server-side invite token
      const updatePayload = { status: 'invited' };
      if (payload.role) updatePayload.role = payload.role;
      if (payload.tenantId) updatePayload.tenantId = payload.tenantId;

      const updateResult = await api.updateUser(existingUser.id, updatePayload).catch(() => ({}));
      // Server returns the user with a fresh inviteToken
      // inviteTokenIssued: the one-time echo. The persistent inviteToken is no longer
      // serialised (it was going out on every user list — a bearer credential for a pending
      // account). Old name kept as a fallback for a cached bundle mid-deploy.
      const serverInviteToken = updateResult?.data?.inviteTokenIssued || updateResult?.inviteTokenIssued
        || updateResult?.data?.inviteToken || updateResult?.inviteToken || '';
      const inviteData = buildInviteData(existingUser.id, { ...payload, email: existingUser.email }, serverInviteToken);
      setInvitations((prev) => [...prev, inviteData]);
      await loadUsers();
      triggerInviteEmailWorkflow(inviteData).catch(() => {});
      return { ok: true, data: inviteData, reInvite: true };
    }

    // ── Step 2: Create new user with status 'invited' ──────────────────
    try {
      const userPayload = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        name: `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
        email: payload.email,
        role: payload.role || 'Viewer',
        status: 'invited',
        tenantId: payload.tenantId,
        password: `Inv!${Date.now()}${Math.random().toString(36).slice(2, 10)}`,
      };

      const result = await api.createUser(userPayload);

      if (result.ok) {
        const userId = result.data?.id || `inv-${Date.now()}`;
        const serverInviteToken = result.data?.inviteTokenIssued || result.data?.inviteToken || '';
        const inviteData = buildInviteData(userId, payload, serverInviteToken);
        setInvitations((prev) => [...prev, inviteData]);
        await loadUsers();

        // Trigger the invite email workflow (fire-and-forget)
        triggerInviteEmailWorkflow(inviteData).then((wfResult) => {
          if (wfResult?.ok) console.info('[invite] Email workflow triggered successfully');
          else console.warn('[invite] Email workflow trigger failed:', wfResult?.error);
        });

        return { ok: true, data: inviteData };
      }

      // Dev mode fallback (no server token in dev mode)
      if (isDevMode()) {
        const inviteData = buildInviteData(`inv-${Date.now()}`, payload, `dev-${Date.now()}`);
        setInvitations((prev) => [...prev, inviteData]);
        triggerInviteEmailWorkflow(inviteData).catch(() => {});
        return { ok: true, data: inviteData };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        const inviteData = buildInviteData(`inv-${Date.now()}`, payload, `dev-${Date.now()}`);
        setInvitations((prev) => [...prev, inviteData]);
        triggerInviteEmailWorkflow(inviteData).catch(() => {});
        return { ok: true, data: inviteData };
      }
      return { ok: false, error: err.message };
    }
  }, [loadUsers, triggerInviteEmailWorkflow, checkUserEmail]);

  // ─── Approval Notification Email Workflow ───────────────────────────────
  // System workflow that sends HTML notification emails during the approval
  // lifecycle: request created, approved, rejected, escalated, dual-awaiting.
  const APPROVAL_EMAIL_WORKFLOW_NAME = 'System: Approval Notification Email';

  const buildApprovalEmailWorkflowDef = () => ({
    nodes: [
      {
        id: 'trigger-manual',
        type: 'manual_trigger',
        label: 'Approval Event Triggered',
        x: 80, y: 200,
        config: {
          description: 'Triggered by the Approval Service when an approval lifecycle event occurs',
          outputVariable: 'trigger-manual',
        },
      },
      {
        id: 'send-approval-email',
        type: 'notification_task',
        label: 'Send Approval Notification',
        x: 400, y: 200,
        config: {
          channel: 'email',
          recipient: '${recipient_email}',
          subject: '${email_subject}',
          content_type: 'html',
          body: [
            '<div style="font-family:Inter,system-ui,sans-serif;max-width:600px;margin:0 auto;background:#fff;border:1px solid #e7e5e4;border-radius:12px;overflow:hidden">',
            '<div style="background:linear-gradient(135deg,#0d9488 0%,#0a766b 100%);padding:32px;text-align:center">',
            '<h1 style="color:#fff;font-size:22px;margin:0">Prove7 Approval Center</h1>',
            '<p style="color:rgba(255,255,255,0.85);font-size:13px;margin:6px 0 0">${event_headline}</p>',
            '</div>',
            '<div style="padding:32px">',
            '<p style="font-size:15px;color:#18181b;margin:0 0 20px">Hi <strong>${recipient_name}</strong>,</p>',
            '<p style="font-size:14px;color:#52525b;line-height:1.6;margin:0 0 20px">${event_message}</p>',
            '<div style="background:#f5f5f4;border-radius:8px;padding:16px;margin:20px 0">',
            '<div style="font-size:12px;color:#71717a;margin-bottom:12px"><strong>Approval Details:</strong></div>',
            '<table style="width:100%;font-size:13px;color:#18181b;border-collapse:collapse">',
            '<tr><td style="padding:4px 0;color:#71717a;width:120px">Request</td><td style="padding:4px 0"><strong>${approval_title}</strong></td></tr>',
            '<tr><td style="padding:4px 0;color:#71717a">Type</td><td style="padding:4px 0">${request_type}</td></tr>',
            '<tr><td style="padding:4px 0;color:#71717a">Source</td><td style="padding:4px 0">${source_name}</td></tr>',
            '<tr><td style="padding:4px 0;color:#71717a">Status</td><td style="padding:4px 0"><strong style="color:${status_color}">${status_label}</strong></td></tr>',
            '<tr><td style="padding:4px 0;color:#71717a">Urgency</td><td style="padding:4px 0">${urgency}</td></tr>',
            '<tr><td style="padding:4px 0;color:#71717a">Requested By</td><td style="padding:4px 0">${requester_name}</td></tr>',
            '${decision_row}',
            '</table>',
            '</div>',
            '${action_section}',
            '<div style="border-top:1px solid #e7e5e4;padding-top:16px;margin-top:24px">',
            '<p style="font-size:11px;color:#a8a29e;margin:0">This notification was sent by the Prove7 Approval System. Visit the <a href="${platform_url}/approvals" style="color:#0d9488;text-decoration:none">Approval Center</a> to manage all pending requests.</p>',
            '</div>',
            '</div>',
            '</div>',
          ].join('\n'),
          priority: '${email_priority}',
        },
      },
    ],
    connections: [
      { id: 'conn_trigger_to_email', from: 'trigger-manual', fromPort: 'default', to: 'send-approval-email', toPort: 'default' },
    ],
  });

  /**
   * Build the input variables for each approval event type.
   * @param {'requested'|'approved'|'rejected'|'escalated'|'awaiting_second'} eventType
   * @param {Object} data - { approvalTitle, requestType, sourceName, urgency, requesterName,
   *                          recipientEmail, recipientName, approverName, comment, statusLabel }
   */
  const buildApprovalEmailInput = (eventType, data) => {
    const base = {
      recipient_email: data.recipientEmail,
      recipient_name: data.recipientName || data.recipientEmail,
      approval_title: data.approvalTitle || 'Approval Request',
      request_type: (data.requestType || 'skill_execution').replace(/_/g, ' '),
      source_name: data.sourceName || '—',
      urgency: data.urgency || 'normal',
      requester_name: data.requesterName || 'System',
      platform_url: window.location.origin,
      decision_row: '',
      email_priority: 'high',
    };

    switch (eventType) {
      case 'requested':
        return {
          ...base,
          email_subject: `[Action Required] New Approval Request: ${data.approvalTitle}`,
          event_headline: 'New Approval Request',
          event_message: `A new approval request requires your review. <strong>${data.requesterName}</strong> has submitted a request that needs your authorization before it can proceed.`,
          status_label: 'Pending Your Review',
          status_color: '#d97706',
          action_section: [
            '<div style="text-align:center;margin:28px 0">',
            `<a href="${window.location.origin}/approvals" style="display:inline-block;padding:14px 36px;background:#0d9488;color:#fff;font-size:14px;font-weight:600;text-decoration:none;border-radius:8px">Review &amp; Approve</a>`,
            '</div>',
          ].join('\n'),
        };
      case 'approved':
        return {
          ...base,
          email_subject: `[Approved] ${data.approvalTitle}`,
          event_headline: 'Request Approved',
          event_message: `Your approval request has been <strong style="color:#059669">approved</strong>${data.approverName ? ' by <strong>' + data.approverName + '</strong>' : ''}.${data.comment ? ' Comment: "' + data.comment + '"' : ''}`,
          status_label: 'Approved',
          status_color: '#059669',
          decision_row: data.approverName ? `<tr><td style="padding:4px 0;color:#71717a">Decided By</td><td style="padding:4px 0">${data.approverName}</td></tr>` : '',
          action_section: '',
          email_priority: 'normal',
        };
      case 'rejected':
        return {
          ...base,
          email_subject: `[Rejected] ${data.approvalTitle}`,
          event_headline: 'Request Rejected',
          event_message: `Your approval request has been <strong style="color:#dc2626">rejected</strong>${data.approverName ? ' by <strong>' + data.approverName + '</strong>' : ''}.${data.comment ? ' Reason: "' + data.comment + '"' : ''}`,
          status_label: 'Rejected',
          status_color: '#dc2626',
          decision_row: data.approverName ? `<tr><td style="padding:4px 0;color:#71717a">Decided By</td><td style="padding:4px 0">${data.approverName}</td></tr>` : '',
          action_section: '',
          email_priority: 'normal',
        };
      case 'escalated':
        return {
          ...base,
          email_subject: `[Escalated] Approval Request Needs Your Attention: ${data.approvalTitle}`,
          event_headline: 'Escalated Approval Request',
          event_message: `An approval request has been <strong style="color:#d97706">escalated</strong> to you because the previous approver did not respond within the allowed time. Your prompt review is required.`,
          status_label: 'Escalated — Needs Review',
          status_color: '#d97706',
          action_section: [
            '<div style="text-align:center;margin:28px 0">',
            `<a href="${window.location.origin}/approvals" style="display:inline-block;padding:14px 36px;background:#d97706;color:#fff;font-size:14px;font-weight:600;text-decoration:none;border-radius:8px">Review Escalated Request</a>`,
            '</div>',
          ].join('\n'),
        };
      case 'awaiting_second':
        return {
          ...base,
          email_subject: `[Dual Approval] Second Approval Needed: ${data.approvalTitle}`,
          event_headline: 'Second Approval Required',
          event_message: `This request has received its first approval and now requires a <strong>second approval</strong> from you to be fully authorized. The first approval was granted${data.approverName ? ' by <strong>' + data.approverName + '</strong>' : ''}.`,
          status_label: 'Awaiting Second Approval',
          status_color: '#7c3aed',
          action_section: [
            '<div style="text-align:center;margin:28px 0">',
            `<a href="${window.location.origin}/approvals" style="display:inline-block;padding:14px 36px;background:#7c3aed;color:#fff;font-size:14px;font-weight:600;text-decoration:none;border-radius:8px">Review &amp; Grant Second Approval</a>`,
            '</div>',
          ].join('\n'),
        };
      default:
        return base;
    }
  };

  /**
   * Find or create the approval email system workflow, then execute it.
   */
  const triggerApprovalEmailWorkflow = useCallback(async (eventType, emailData) => {
    try {
      // System workflows live in the dedicated Agentlet System Org/Tenant
      const wfOrgId = SYSTEM_ORG_UUID;
      const wfTenantId = SYSTEM_TENANT_UUID;

      // Find or create the system workflow.
      // V132 — archived:'all' so a mistakenly-archived approval system
      // workflow is still located by name (see invite workflow for rationale).
      let workflowId = null;
      const wfResult = await api.getWorkflows({ orgId: wfOrgId, tenantId: wfTenantId, archived: 'all' });
      if (wfResult.ok) {
        const existing = (wfResult.data || []).find((w) => w.name === APPROVAL_EMAIL_WORKFLOW_NAME);
        if (existing) workflowId = existing.id;
      }

      if (!workflowId) {
        const createResult = await api.createWorkflow({
          name: APPROVAL_EMAIL_WORKFLOW_NAME,
          description: 'System workflow: sends approval notification emails during the approval lifecycle (requested, approved, rejected, escalated, dual-awaiting). Triggered automatically by the Approval Service.',
          status: 'active',
          orgId: wfOrgId,
          tenantId: wfTenantId,
          definition: buildApprovalEmailWorkflowDef(),
        });
        if (createResult?.ok) workflowId = createResult.data?.id;
      } else {
        await api.updateWorkflow(workflowId, { definition: buildApprovalEmailWorkflowDef() }).catch(() => {});
      }

      if (!workflowId) {
        console.warn('[approval-email] Could not find or create approval email workflow');
        return { ok: false, error: 'Workflow not available' };
      }

      // Build the email input variables based on event type
      const input = buildApprovalEmailInput(eventType, emailData);

      const execResult = await api.executeWorkflow(workflowId, { input });
      return execResult;
    } catch (err) {
      console.warn('[approval-email] Failed to trigger approval email workflow:', err.message);
      return { ok: false, error: err.message };
    }
  }, [orgs]);

  const loadInvitations = useCallback(async () => {
    try {
      const result = await api.getInvitations();
      if (result.ok) {
        setInvitations(result.data || []);
      }
    } catch (err) {
      console.error('Error loading invitations:', err);
    }
  }, []);

  const revokeInvitation = useCallback(async (invitationId) => {
    try {
      const result = await api.revokeInvitation(invitationId);
      if (result.ok) {
        setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
        return result;
      }
      if (isDevMode()) {
        setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
        return { ok: true };
      }
      return result;
    } catch (err) {
      if (isDevMode()) {
        setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
        return { ok: true };
      }
      return { ok: false, error: err.message };
    }
  }, []);

  // ─── Approval Center ────────────────────────────────────────────────
  const [approvalInbox, setApprovalInbox] = useState([]);
  const [approvalSubmitted, setApprovalSubmitted] = useState([]);
  const [approvalAll, setApprovalAll] = useState([]);
  const [approvalMyRole, setApprovalMyRole] = useState([]); // PROVE7-040 role-queue oversight
  const [approvalPolicies, setApprovalPolicies] = useState([]);
  const [approvalStats, setApprovalStats] = useState({});
  const [approvalInboxCount, setApprovalInboxCount] = useState(0);

  const loadApprovalInbox = useCallback(async () => {
    try {
      const result = await api.getApprovalInbox();
      const data = result?.data || result;
      setApprovalInbox(Array.isArray(data) ? data : []);
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const loadApprovalInboxCount = useCallback(async () => {
    try {
      const result = await api.getApprovalInboxCount();
      const data = result?.data || result;
      setApprovalInboxCount(data?.count || 0);
      return result;
    } catch (err) { setApprovalInboxCount(0); return { ok: false, error: err.message }; }
  }, []);

  const loadApprovalSubmitted = useCallback(async () => {
    try {
      const result = await api.getApprovalSubmitted();
      const data = result?.data || result;
      setApprovalSubmitted(Array.isArray(data) ? data : []);
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  // PROVE7-040 — the role-holder's oversight of their role's queue (all statuses). Keeps a
  // decided role-queue approval visible after it leaves the actionable inbox.
  const loadApprovalMyRole = useCallback(async () => {
    try {
      const result = await api.getMyRoleApprovals();
      const data = result?.data || result;
      setApprovalMyRole(Array.isArray(data) ? data : []);
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const loadApprovalAll = useCallback(async ({ orgId, tenantId, status } = {}) => {
    try {
      const result = await api.getApprovals({ orgId, tenantId, status });
      const data = result?.data || result;
      setApprovalAll(Array.isArray(data) ? data : []);
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const loadApprovalPolicies = useCallback(async ({ orgId, tenantId } = {}) => {
    try {
      const result = await api.getApprovalPolicies({ orgId, tenantId });
      const data = result?.data || result;
      setApprovalPolicies(Array.isArray(data) ? data : []);
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  const loadApprovalStats = useCallback(async ({ orgId, tenantId } = {}) => {
    try {
      const result = await api.getApprovalStats({ orgId, tenantId });
      const data = result?.data || result;
      setApprovalStats(data || {});
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, []);

  /**
   * Helper: resolve user name from ID for email notifications.
   */
  const resolveUserName = useCallback((userId) => {
    if (!userId) return 'System';
    const u = (users || []).find((x) => x.id === userId);
    return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : userId;
  }, [users]);

  const resolveUserEmail = useCallback((userId) => {
    if (!userId) return null;
    const u = (users || []).find((x) => x.id === userId);
    return u?.email || null;
  }, [users]);

  /**
   * Helper: send approval email notifications to both issuer and approver.
   */
  const sendApprovalEmails = useCallback(async (eventType, reqData, comment) => {
    try {
      const emailBase = {
        approvalTitle: reqData.title || reqData.sourceName || 'Approval Request',
        requestType: reqData.requestType,
        sourceName: reqData.sourceName || '—',
        urgency: reqData.urgency || 'normal',
        requesterName: resolveUserName(reqData.requestedBy),
        orgId: reqData.orgId,
        tenantId: reqData.tenantId,
        comment,
      };

      // Determine recipients based on event type
      if (eventType === 'requested' || eventType === 'escalated' || eventType === 'awaiting_second') {
        // Notify the assigned approver
        const approverTo = eventType === 'awaiting_second' ? reqData.secondAssignedTo : reqData.assignedTo;
        const approverEmail = resolveUserEmail(approverTo);
        if (approverEmail) {
          triggerApprovalEmailWorkflow(eventType, {
            ...emailBase,
            recipientEmail: approverEmail,
            recipientName: resolveUserName(approverTo),
          }).catch(() => {});
        }
      } else if (eventType === 'approved' || eventType === 'rejected') {
        // Notify the original requester (issuer)
        const issuerEmail = resolveUserEmail(reqData.requestedBy);
        const approverName = resolveUserName(reqData.firstDecidedBy || reqData.secondDecidedBy);
        if (issuerEmail) {
          triggerApprovalEmailWorkflow(eventType, {
            ...emailBase,
            recipientEmail: issuerEmail,
            recipientName: resolveUserName(reqData.requestedBy),
            approverName,
          }).catch(() => {});
        }
        // Also notify the approver as confirmation
        const decidedById = reqData.secondDecidedBy || reqData.firstDecidedBy;
        const approverEmail = resolveUserEmail(decidedById);
        if (approverEmail && approverEmail !== issuerEmail) {
          triggerApprovalEmailWorkflow(eventType, {
            ...emailBase,
            recipientEmail: approverEmail,
            recipientName: resolveUserName(decidedById),
            approverName: resolveUserName(decidedById),
          }).catch(() => {});
        }
      }
    } catch (err) {
      console.warn('[approval-email] Failed to send notification:', err.message);
    }
  }, [resolveUserName, resolveUserEmail, triggerApprovalEmailWorkflow]);

  const approveApprovalRequest = useCallback(async (requestId, comment) => {
    try {
      const result = await api.approveApprovalRequest(requestId, comment);
      if (result.ok) {
        loadApprovalInbox(); loadApprovalInboxCount();
        const req = result?.data || result;
        const eventType = req.status === 'awaiting_second' ? 'awaiting_second' : 'approved';
        sendApprovalEmails(eventType, req, comment);
      }
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, [loadApprovalInbox, loadApprovalInboxCount, sendApprovalEmails]);

  const rejectApprovalRequest = useCallback(async (requestId, comment) => {
    try {
      const result = await api.rejectApprovalRequest(requestId, comment);
      if (result.ok) {
        loadApprovalInbox(); loadApprovalInboxCount();
        const req = result?.data || result;
        sendApprovalEmails('rejected', req, comment);
      }
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, [loadApprovalInbox, loadApprovalInboxCount, sendApprovalEmails]);

  const cancelApprovalRequest = useCallback(async (requestId) => {
    try {
      const result = await api.cancelApprovalRequest(requestId);
      if (result.ok) { loadApprovalSubmitted(); loadApprovalInboxCount(); }
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, [loadApprovalSubmitted, loadApprovalInboxCount]);

  const createApprovalPolicy = useCallback(async (policy) => {
    try {
      const result = await api.createApprovalPolicy(policy);
      if (result.ok) loadApprovalPolicies();
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, [loadApprovalPolicies]);

  const updateApprovalPolicy = useCallback(async (policyId, updates) => {
    try {
      const result = await api.updateApprovalPolicy(policyId, updates);
      if (result.ok) loadApprovalPolicies();
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, [loadApprovalPolicies]);

  const deleteApprovalPolicy = useCallback(async (policyId) => {
    try {
      const result = await api.deleteApprovalPolicy(policyId);
      if (result.ok) loadApprovalPolicies();
      return result;
    } catch (err) { return { ok: false, error: err.message }; }
  }, [loadApprovalPolicies]);

  /* RC-92a — STRUCTURAL FIX for the entire RC-91 bug class.
   *
   * Pre-RC-92a, `value` was constructed inline every render of
   * PlatformProvider. That made it a fresh object reference on every
   * parent re-render even when nothing in this provider's state had
   * changed. Any consumer with `[platform]` (or any callback from it)
   * in a useEffect dep array re-fired on every render — and if that
   * effect called setState back into the platform, we had an infinite
   * loop (RC-91: hundreds of /api/v1/agents requests per second).
   *
   * useMemo with EVERY state slot and EVERY exposed callback as
   * deps means the object identity only churns when one of those
   * actually changed. Since all callbacks below are already wrapped
   * in useCallback with stable deps (verified — most are `[]`, a
   * handful depend on other stable callbacks or on user/org state),
   * the dep array is itself stable across renders. Net result:
   *   - Renders not caused by platform-state change → same object,
   *     consumers' [platform] effects DO NOT re-fire.
   *   - Renders from a platform setState → new object, consumers
   *     re-fire (correct, expected behavior).
   *
   * Adding a new field to the value object? Append it both inside
   * the returned object AND to the deps array below. The two lists
   * MUST stay in sync, or the new field will not propagate updates
   * to consumers. */
  const value = useMemo(() => ({
    agents,
    skills,
    tenants,
    deployments,
    connectors,
    executions,
    users,
    groups,
    orgs,
    dashboardMetrics,
    inferenceOverrides,
    inferenceStats,
    inferenceAuditLog,
    // Lifecycle state
    phaseGates,
    skillReviews,
    assignments,
    publications,
    loading,
    error,
    loadAgents,
    loadDashboardMetrics,
    loadEvals,
    generateEval,
    runEval,
    getEvalTestCases,
    getEvalSuite,
    getEvalRuns,
    updateEvalSuite,
    updateTestCase,
    getLlmProviders,
    deleteEvalSuite,
    loadExecutions,
    loadTenants,
    loadDeployments,
    loadConnectors,
    loadUsers,
    loadGroups,
    createUser,
    updateUser,
    deleteUser,
    createGroup,
    updateGroup,
    deleteGroup,
    addGroupMember,
    removeGroupMember,
    loadInferenceData,
    createAgent,
    updateAgent,
    deleteAgent,
    addSkill,
    updateSkill,
    removeSkill,
    unpublishSkill,
    pushSkill,
    pushAllSkills,
    syncToAgentlet,
    promoteSkill,
    // Workflow & Orchestration
    workflows,
    workflowRuns,
    orchestrationStats,
    loadWorkflows,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    saveCanvas,
    executeWorkflow,
    loadWorkflowRuns,
    cancelWorkflowRun,
    loadOrchestrationStats,
    // Lifecycle actions
    advancePhase,
    advanceSkill,
    regressSkill,
    loadPhaseGates,
    reviewSkill,
    loadSkillReviews,
    loadAssignments,
    createAssignment,
    removeAssignment,
    publishAgent,
    loadPublications,
    rollbackPublication,
    createDeployment,
    updateDeployment,
    deleteDeployment,
    updateDeploymentStatus,
    loadDeploymentHierarchy,
    deployFromAgent,
    loadDeploymentStats,
    loadDeploymentHealth,
    // Instances
    instances,
    loadInstances,
    createInstance,
    updateInstance,
    deleteInstance,
    // Organizations
    loadOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    createConnector,
    updateConnector,
    deleteConnector,
    testConnector,
    testConnectorUrl,
    loadConnectorStats,
    loadSkillConnectors,
    linkSkillConnector,
    unlinkSkillConnector,
    loadConnectorSkills,
    createTenant,
    updateTenant,
    deleteTenant,
    approveExecution,
    rejectExecution,
    activateKillSwitch,
    deactivateKillSwitch,
    setInferenceMode,
    setSkillInferenceMode,
    // Connector Groups & Agent-Connector Assignments
    connectorGroups,
    connectorAssignments,
    loadConnectorGroups,
    createConnectorGroup,
    updateConnectorGroup,
    deleteConnectorGroup,
    addConnectorGroupMember,
    removeConnectorGroupMember,
    loadConnectorAssignments,
    createConnectorAssignment,
    removeConnectorAssignment,
    getAssignedConnectorIds,
    // Invitations
    invitations,
    sendInvitation,
    triggerInviteEmailWorkflow,
    triggerApprovalEmailWorkflow,
    checkUserEmail,
    loadInvitations,
    revokeInvitation,
    // Approval Center
    approvalInbox,
    approvalSubmitted,
    approvalAll,
    approvalMyRole,
    approvalPolicies,
    approvalStats,
    approvalInboxCount,
    loadApprovalInbox,
    loadApprovalInboxCount,
    loadApprovalSubmitted,
    loadApprovalMyRole,
    loadApprovalAll,
    loadApprovalPolicies,
    loadApprovalStats,
    approveApprovalRequest,
    rejectApprovalRequest,
    cancelApprovalRequest,
    createApprovalPolicy,
    updateApprovalPolicy,
    deleteApprovalPolicy,
  }), [
    // ── State slots ─────────────────────────────────────────────
    agents, skills, tenants, deployments, connectors, executions,
    users, groups, orgs,
    dashboardMetrics, inferenceOverrides, inferenceStats, inferenceAuditLog,
    phaseGates, skillReviews, assignments, publications, loading, error,
    workflows, workflowRuns, orchestrationStats,
    instances,
    connectorGroups, connectorAssignments,
    invitations,
    approvalInbox, approvalSubmitted, approvalAll, approvalMyRole, approvalPolicies,
    approvalStats, approvalInboxCount,
    // ── Memoized callbacks ──────────────────────────────────────
    loadAgents, loadDashboardMetrics, loadEvals, generateEval, runEval,
    getEvalTestCases, getEvalSuite, getEvalRuns, updateEvalSuite,
    updateTestCase, getLlmProviders, deleteEvalSuite, loadExecutions,
    loadTenants, loadDeployments, loadConnectors, loadUsers, loadGroups,
    createUser, updateUser, deleteUser, createGroup, updateGroup,
    deleteGroup, addGroupMember, removeGroupMember, loadInferenceData,
    createAgent, updateAgent, deleteAgent, addSkill, updateSkill,
    removeSkill, unpublishSkill, pushSkill, pushAllSkills, syncToAgentlet,
    promoteSkill,
    loadWorkflows, createWorkflow, updateWorkflow, deleteWorkflow,
    saveCanvas, executeWorkflow, loadWorkflowRuns, cancelWorkflowRun,
    loadOrchestrationStats,
    advancePhase, advanceSkill, regressSkill, loadPhaseGates, reviewSkill,
    loadSkillReviews, loadAssignments, createAssignment, removeAssignment,
    publishAgent, loadPublications, rollbackPublication,
    createDeployment, updateDeployment, deleteDeployment,
    updateDeploymentStatus, loadDeploymentHierarchy, deployFromAgent,
    loadDeploymentStats, loadDeploymentHealth,
    loadInstances, createInstance, updateInstance, deleteInstance,
    loadOrganizations, createOrganization, updateOrganization,
    deleteOrganization,
    createConnector, updateConnector, deleteConnector, testConnector,
    testConnectorUrl, loadConnectorStats, loadSkillConnectors,
    linkSkillConnector, unlinkSkillConnector, loadConnectorSkills,
    createTenant, updateTenant, deleteTenant,
    approveExecution, rejectExecution, activateKillSwitch,
    deactivateKillSwitch, setInferenceMode, setSkillInferenceMode,
    loadConnectorGroups, createConnectorGroup, updateConnectorGroup,
    deleteConnectorGroup, addConnectorGroupMember, removeConnectorGroupMember,
    loadConnectorAssignments, createConnectorAssignment,
    removeConnectorAssignment, getAssignedConnectorIds,
    sendInvitation, triggerInviteEmailWorkflow, triggerApprovalEmailWorkflow,
    checkUserEmail, loadInvitations, revokeInvitation,
    loadApprovalInbox, loadApprovalInboxCount, loadApprovalSubmitted,
    loadApprovalMyRole, loadApprovalAll, loadApprovalPolicies, loadApprovalStats,
    approveApprovalRequest, rejectApprovalRequest, cancelApprovalRequest,
    createApprovalPolicy, updateApprovalPolicy, deleteApprovalPolicy,
  ]);

  return (
    <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
  );
}

export function usePlatform() {
  const context = React.useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within PlatformProvider');
  }
  return context;
}
